import { useEffect, useMemo, useRef, useState, type DragEvent, type MouseEvent } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Popover,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import AddIdeaPopup from '../../idea-input/AddIdeaPopup';
import IdeaCard from '../../cards/IdeaCard';

import type { BoardColumn } from '../../../interfaces/BoardDetails';
import type { Idea } from '../../../interfaces/Idea';
import { canAddCardsToBoardColumn, sanitizeBoardColumnTitle } from '../../../utils/boardColumns';
import {
  getCardOrderWithCardMovedToColumnPosition,
  getCardOrderWithColumnOrder,
  getColumnIdeasByColumnId,
  getCustomOrderedBoardIdeas,
  getOrderedBoardIdeas,
  moveCardOrderItem,
  type RandomOrderRef,
} from '../../../utils/boardCardOrdering';
import type { RankBy } from '../../../types/boardCardOrdering';

const hasFilesInTransfer = (event: DragEvent<HTMLElement>) =>
  Array.from(event.dataTransfer?.types ?? []).includes('Files');

type ColumnBoardCardsProps = {
  ideas: Idea[];
  columns: BoardColumn[];
  rankBy: RankBy;
  randomizeCardPosition: boolean;
  randomOrderRef: RandomOrderRef;
  moveEnabled: boolean;
  canAddCards: boolean;
  canEditColumns: boolean;
  onMoveIdea: (ideaId: string, columnId: string) => Promise<void>;
  onReorder: (cardOrder: string[]) => Promise<void>;
  onUpdateColumn: (
    columnId: string,
    updates: { title?: string; allowCardCreation?: boolean }
  ) => Promise<void>;
  onDropFilesToColumn: (files: File[], columnId: string) => Promise<void>;
  customSortEnabled?: boolean;
};

const ColumnBoardCards = ({
  ideas,
  columns,
  rankBy,
  randomizeCardPosition,
  randomOrderRef,
  moveEnabled,
  canAddCards,
  canEditColumns,
  onMoveIdea,
  onReorder,
  onUpdateColumn,
  onDropFilesToColumn,
  customSortEnabled = false,
}: ColumnBoardCardsProps) => {
  const [draggingIdeaId, setDraggingIdeaId] = useState<string | null>(null);
  const [dragCardOrder, setDragCardOrder] = useState<string[]>([]);
  const [dragPreviewColumnId, setDragPreviewColumnId] = useState<string | null>(null);
  const [editingColumnId, setEditingColumnId] = useState<string | null>(null);
  const [columnTitleDraft, setColumnTitleDraft] = useState('');
  const [columnAllowCardCreationDraft, setColumnAllowCardCreationDraft] = useState(true);
  const [columnSettingsAnchorEl, setColumnSettingsAnchorEl] = useState<HTMLElement | null>(null);
  const dragStartCardOrderRef = useRef<string[]>([]);
  const dragCardOrderRef = useRef<string[]>([]);
  const draggingIdeaIdRef = useRef<string | null>(null);
  const draggingColumnIdRef = useRef<string | null>(null);
  const dragPreviewColumnIdRef = useRef<string | null>(null);
  const didDragReorderRef = useRef(false);

  const orderedIdeas = useMemo(
    () =>
      customSortEnabled
        ? getCustomOrderedBoardIdeas(ideas)
        : getOrderedBoardIdeas(ideas, rankBy, randomizeCardPosition, randomOrderRef),
    [customSortEnabled, ideas, randomOrderRef, randomizeCardPosition, rankBy]
  );

  useEffect(() => {
    if (!customSortEnabled || draggingIdeaId) {
      return;
    }

    const nextCardOrder = orderedIdeas.map((idea) => idea.id);
    dragCardOrderRef.current = nextCardOrder;
    setDragCardOrder(nextCardOrder);
  }, [customSortEnabled, draggingIdeaId, orderedIdeas]);

  const dragOrderedIdeas = useMemo(() => {
    if (!customSortEnabled || dragCardOrder.length === 0) {
      return orderedIdeas;
    }

    const visibleIdeasById = new Map(orderedIdeas.map((idea) => [idea.id, idea]));
    const sortedVisibleIdeas = dragCardOrder
      .map((ideaId) => visibleIdeasById.get(ideaId))
      .filter((idea): idea is Idea => Boolean(idea));

    if (sortedVisibleIdeas.length === 0) {
      return orderedIdeas;
    }

    const sortedIdeaIds = new Set(sortedVisibleIdeas.map((idea) => idea.id));
    const missingIdeas = orderedIdeas.filter((idea) => !sortedIdeaIds.has(idea.id));

    return [...sortedVisibleIdeas, ...missingIdeas];
  }, [customSortEnabled, dragCardOrder, orderedIdeas]);

  const displayOrderedIdeas = useMemo(() => {
    if (!customSortEnabled || !draggingIdeaId || !dragPreviewColumnId) {
      return dragOrderedIdeas;
    }

    return dragOrderedIdeas.map((idea) =>
      idea.id === draggingIdeaId ? { ...idea, columnId: dragPreviewColumnId } : idea
    );
  }, [customSortEnabled, dragOrderedIdeas, dragPreviewColumnId, draggingIdeaId]);

  const ideasByColumnId = useMemo(() => {
    return getColumnIdeasByColumnId(displayOrderedIdeas, columns);
  }, [columns, displayOrderedIdeas]);

  const handleDragStart = (event: DragEvent<HTMLElement>, idea: Idea, columnId: string) => {
    if (!moveEnabled) {
      return;
    }

    event.stopPropagation();
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', idea.id);

    if (customSortEnabled) {
      const baseCardOrder = dragOrderedIdeas.map((orderedIdea) => orderedIdea.id);
      dragStartCardOrderRef.current = baseCardOrder;
      dragCardOrderRef.current = baseCardOrder;
      draggingIdeaIdRef.current = idea.id;
      draggingColumnIdRef.current = columnId;
      dragPreviewColumnIdRef.current = columnId;
      didDragReorderRef.current = false;
      setDragPreviewColumnId(columnId);
      setDragCardOrder(baseCardOrder);
    }

    setDraggingIdeaId(idea.id);
  };

  const handleColumnDragOver = (event: DragEvent<HTMLElement>, columnId: string) => {
    if (hasFilesInTransfer(event)) {
      if (!canAddCardsToBoardColumn(columns, columnId, canAddCards)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = 'copy';
      return;
    }

    if (!moveEnabled || !draggingIdeaId) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';

    if (!customSortEnabled) {
      return;
    }

    if (draggingColumnIdRef.current === columnId && dragPreviewColumnIdRef.current === columnId) {
      return;
    }

    const nextCardOrder = getCardOrderWithCardMovedToColumnPosition({
      orderedIdeas: dragOrderedIdeas,
      columns,
      draggedCardId: draggingIdeaId,
      targetColumnId: columnId,
    });

    didDragReorderRef.current = true;
    dragCardOrderRef.current = nextCardOrder;
    dragPreviewColumnIdRef.current = columnId;
    setDragPreviewColumnId(columnId);
    setDragCardOrder(nextCardOrder);
  };

  const handleIdeaDragOver = (
    event: DragEvent<HTMLElement>,
    targetIdeaId: string,
    columnId: string
  ) => {
    if (!customSortEnabled || !draggingIdeaId) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'move';

    if (targetIdeaId === draggingIdeaId) {
      return;
    }

    const nextCardOrder =
      draggingColumnIdRef.current === columnId
        ? getCardOrderWithColumnOrder({
            orderedIdeas: dragOrderedIdeas,
            columns,
            columnId,
            columnCardOrder: moveCardOrderItem(
              getColumnIdeasByColumnId(
                dragOrderedIdeas.map((idea) =>
                  idea.id === draggingIdeaId ? { ...idea, columnId } : idea
                ),
                columns
              )
                .get(columnId)
                ?.map((idea) => idea.id) ?? [],
              draggingIdeaId,
              targetIdeaId
            ),
          })
        : getCardOrderWithCardMovedToColumnPosition({
            orderedIdeas: dragOrderedIdeas,
            columns,
            draggedCardId: draggingIdeaId,
            targetColumnId: columnId,
            targetCardId: targetIdeaId,
          });

    didDragReorderRef.current = true;
    dragCardOrderRef.current = nextCardOrder;
    dragPreviewColumnIdRef.current = columnId;
    setDragPreviewColumnId(columnId);
    setDragCardOrder(nextCardOrder);
  };

  const clearCustomSortDrag = () => {
    dragStartCardOrderRef.current = [];
    dragCardOrderRef.current = [];
    draggingIdeaIdRef.current = null;
    draggingColumnIdRef.current = null;
    dragPreviewColumnIdRef.current = null;
    didDragReorderRef.current = false;
    setDragPreviewColumnId(null);
  };

  const finalizeCustomSortDrag = () => {
    if (!customSortEnabled || !draggingIdeaIdRef.current) {
      setDraggingIdeaId(null);
      return;
    }

    const currentCardOrder =
      dragCardOrderRef.current.length > 0
        ? dragCardOrderRef.current
        : dragOrderedIdeas.map((idea) => idea.id);
    const startCardOrder = dragStartCardOrderRef.current;
    const didChange =
      currentCardOrder.length !== startCardOrder.length ||
      currentCardOrder.some((cardId, index) => cardId !== startCardOrder[index]);
    const shouldPersist = didDragReorderRef.current && didChange;

    if (shouldPersist) {
      void onReorder(currentCardOrder);
    }

    setDraggingIdeaId(null);
    clearCustomSortDrag();
  };

  const handleColumnDrop = (event: DragEvent<HTMLElement>, columnId: string) => {
    if (hasFilesInTransfer(event)) {
      if (!canAddCardsToBoardColumn(columns, columnId, canAddCards)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const droppedFiles = Array.from(event.dataTransfer.files ?? []);
      if (droppedFiles.length > 0) {
        void onDropFilesToColumn(droppedFiles, columnId);
      }
      return;
    }

    if (!moveEnabled) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    const ideaId = event.dataTransfer.getData('text/plain') || draggingIdeaId;
    const isCustomSameColumnDrop =
      customSortEnabled && Boolean(ideaId) && draggingColumnIdRef.current === columnId;
    const shouldMoveIdea =
      Boolean(ideaId) && (!customSortEnabled || draggingColumnIdRef.current !== columnId);

    if (isCustomSameColumnDrop) {
      finalizeCustomSortDrag();
      return;
    }

    if (!shouldMoveIdea || !ideaId) {
      setDraggingIdeaId(null);
      return;
    }

    if (customSortEnabled) {
      const currentCardOrder =
        dragCardOrderRef.current.length > 0
          ? dragCardOrderRef.current
          : getCardOrderWithCardMovedToColumnPosition({
              orderedIdeas: dragOrderedIdeas,
              columns,
              draggedCardId: ideaId,
              targetColumnId: columnId,
            });

      setDraggingIdeaId(null);
      clearCustomSortDrag();
      void (async () => {
        await onMoveIdea(ideaId, columnId);
        await onReorder(currentCardOrder);
      })();
      return;
    }

    setDraggingIdeaId(null);
    clearCustomSortDrag();
    void onMoveIdea(ideaId, columnId);
  };

  const startEditingColumn = (event: MouseEvent<HTMLElement>, column: BoardColumn) => {
    if (!canEditColumns) {
      return;
    }

    setEditingColumnId(column.id);
    setColumnTitleDraft(column.title);
    setColumnAllowCardCreationDraft(column.allowCardCreation !== false);
    setColumnSettingsAnchorEl(event.currentTarget);
  };

  const cancelEditingColumn = () => {
    setEditingColumnId(null);
    setColumnTitleDraft('');
    setColumnAllowCardCreationDraft(true);
    setColumnSettingsAnchorEl(null);
  };

  const saveEditingColumn = () => {
    const column = columns.find((candidate) => candidate.id === editingColumnId);
    if (!column) {
      cancelEditingColumn();
      return;
    }

    const sanitizedTitle = sanitizeBoardColumnTitle(columnTitleDraft);

    if (!sanitizedTitle) {
      return;
    }

    cancelEditingColumn();

    if (
      sanitizedTitle === column.title &&
      columnAllowCardCreationDraft === (column.allowCardCreation !== false)
    ) {
      return;
    }

    void onUpdateColumn(column.id, {
      title: sanitizedTitle,
      allowCardCreation: columnAllowCardCreationDraft,
    });
  };

  const editingColumn = columns.find((column) => column.id === editingColumnId) ?? null;

  return (
    <Box
      onDragEndCapture={finalizeCustomSortDrag}
      sx={{
        width: '100%',
        height: { xs: 'auto', md: '100%' },
        minHeight: { md: 0 },
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          md: `repeat(${columns.length}, minmax(220px, 1fr))`,
        },
        alignItems: 'stretch',
        gap: 2,
      }}
    >
      {columns.map((column) => {
        const columnIdeas = ideasByColumnId.get(column.id) ?? [];
        const columnCanAddCards = canAddCardsToBoardColumn(columns, column.id, canAddCards);

        return (
          <Stack
            key={column.id}
            spacing={1.5}
            onDragOver={(event) => handleColumnDragOver(event, column.id)}
            onDrop={(event) => handleColumnDrop(event, column.id)}
            sx={{
              height: { xs: 'auto', md: '100%' },
              minHeight: { xs: 220, md: 0 },
              minWidth: 0,
              border: 1,
              borderColor: draggingIdeaId ? 'primary.light' : '#d8dee8',
              borderRadius: 1,
              backgroundColor: '#f5f7fa',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.65)',
              overflow: { xs: 'visible', md: 'hidden' },
            }}
          >
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{
                px: 1.5,
                py: 1.25,
                minHeight: 52,
                borderBottom: 1,
                borderColor: '#d8dee8',
                backgroundColor: '#eef2f6',
              }}
            >
              <>
                <Stack
                  direction="row"
                  spacing={0.25}
                  alignItems="center"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      minWidth: 0,
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {column.title}
                  </Typography>
                  {canEditColumns && (
                    <Tooltip title="Edit column settings">
                      <IconButton
                        size="small"
                        aria-label={`Edit ${column.title} column settings`}
                        onClick={(event) => startEditingColumn(event, column)}
                      >
                        <EditOutlinedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
                <Box
                  sx={{
                    flex: '0 0 auto',
                    ml: 'auto',
                    visibility: columnCanAddCards ? 'visible' : 'hidden',
                    pointerEvents: columnCanAddCards ? 'auto' : 'none',
                  }}
                >
                  <AddIdeaPopup
                    canAddCards={columnCanAddCards}
                    columnId={column.id}
                    buttonLabel="Add a Card"
                    buttonSize="medium"
                    buttonVariant="contained"
                  />
                </Box>
              </>
            </Stack>
            <Stack
              spacing={1.5}
              sx={{
                flex: { md: 1 },
                minHeight: { md: 0 },
                overflowY: { xs: 'visible', md: 'auto' },
                overflowX: 'hidden',
                p: 2,
                pt: 1,
              }}
            >
              {columnIdeas.map((idea) => (
                <Box
                  key={idea.id}
                  draggable={moveEnabled}
                  onDragStart={(event) => handleDragStart(event, idea, column.id)}
                  onDragOver={(event) => handleIdeaDragOver(event, idea.id, column.id)}
                  onDragEnd={finalizeCustomSortDrag}
                  sx={{
                    opacity: draggingIdeaId === idea.id ? 0.45 : 1,
                    cursor: moveEnabled ? 'grab' : 'default',
                    '&:active': {
                      cursor: moveEnabled ? 'grabbing' : 'default',
                    },
                  }}
                >
                  <IdeaCard ideaId={idea.id} />
                </Box>
              ))}
            </Stack>
          </Stack>
        );
      })}
      <Popover
        open={Boolean(editingColumn && columnSettingsAnchorEl)}
        anchorEl={columnSettingsAnchorEl}
        onClose={cancelEditingColumn}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Stack spacing={2} sx={{ p: 2, width: 300 }}>
          <TextField
            label="Column title"
            value={columnTitleDraft}
            onChange={(event) => setColumnTitleDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                saveEditingColumn();
              }
              if (event.key === 'Escape') {
                event.preventDefault();
                cancelEditingColumn();
              }
            }}
            inputProps={{ maxLength: 60 }}
            size="small"
            autoFocus
            fullWidth
          />
          <FormControlLabel
            control={
              <Switch
                checked={columnAllowCardCreationDraft}
                onChange={(event) => setColumnAllowCardCreationDraft(event.target.checked)}
              />
            }
            label="Allow cards to be added"
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={cancelEditingColumn}>Cancel</Button>
            <Button
              variant="contained"
              onClick={saveEditingColumn}
              disabled={!sanitizeBoardColumnTitle(columnTitleDraft)}
            >
              Save
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </Box>
  );
};

export default ColumnBoardCards;
